import { useState, useEffect, useRef } from "react";
import { deleteFlow, getFlows, getFlowsStatistics, exportAll, importFlowsAndTools } from "../api";
import {
  Button,
  Loader,
  NumberFormatter,
  Table,
  Title,
  Center,
  Group,
  Text,
  Container,
  Code,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { useLocation } from "wouter";
import ReactTimeago from "react-timeago-i18n";
import { Sparkline } from "@mantine/charts";
import { IconTrash, IconUpload, IconDownload } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";

export default function FlowList() {
  const [flows, setFlows] = useState([]);
  const [sparklines, setSparklines] = useState({});
  const [summaries, setSummaries] = useState({});
  const [, setLocation] = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  const loadFlows = () => {
    getFlows().then((data) => {
      setFlows(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    // Load flows immediately
    loadFlows();

    // Load statistics separately
    getFlowsStatistics().then((stats) => {
      setSparklines(stats.sparklines ?? {});
      setSummaries(stats.summaries ?? {});
      setIsLoadingStats(false);
    });
  }, []);

  const handleExport = async () => {
    try {
      const data = await exportAll();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `promptshuttle-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notifications.show({
        title: "Export successful",
        message: `Exported ${data.flows?.length || 0} flows and ${data.tools?.length || 0} tools`,
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Export failed",
        message: error.message,
        color: "red",
      });
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.flows && !data.tools) {
        throw new Error("Invalid import file: missing flows or tools array");
      }

      const result = await importFlowsAndTools({
        flows: data.flows || [],
        tools: data.tools || [],
      });

      const skipped = [...(result.skippedFlows || []), ...(result.skippedTools || [])];
      if (skipped.length > 0) {
        notifications.show({
          title: "Some items were skipped (already exist)",
          message: skipped.join(", "),
          color: "yellow",
          autoClose: 8000,
        });
      }

      notifications.show({
        title: "Import successful",
        message: `Imported ${result.flowsImported} flows and ${result.toolsImported} tools`,
        color: "green",
      });

      // Reload the flows list
      loadFlows();
    } catch (error) {
      notifications.show({
        title: "Import failed",
        message: error.message,
        color: "red",
      });
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openConfirmModal = (callback) =>
    modals.openConfirmModal({
      title: "Really delete this flow?",
      children: (
        <Text size="sm">
          This will delete the flow and all its versions and prompts! Are you
          sure you want to do this?
        </Text>
      ),
      labels: { confirm: "Confirm", cancel: "Cancel" },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => callback(),
    });

  return (
    <Container size="lg">
      <Title order={1}>Flows</Title>
      <Group justify="space-between" align="flex-start" mb="md">
        <Text style={{ flex: 1 }}>
          Flows are groups of prompt templates that make collaboration between
          engineering teams and domain experts easier. Read more in the
          documentation.
        </Text>
        <Group>
          <input
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={(e) => handleImportFile(e.target.files?.[0])}
          />
          <Button
            variant="subtle"
            leftSection={<IconDownload size={16} />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="subtle"
            leftSection={<IconUpload size={16} />}
            loading={isImporting}
            onClick={() => fileInputRef.current?.click()}
          >
            Import
          </Button>
          <Button variant="outline" onClick={() => setLocation("/flows/create")}>
            New flow
          </Button>
        </Group>
      </Group>

      {isLoading ? (
        <Center>
          <Loader size="xl" mt="xl" />
        </Center>
      ) : (
        <Table highlightOnHover stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            {/* Eight columns do not fit a phone, and nothing was stopping them from trying —
                the table simply ran off the right edge, taking Spend and Activity with it while
                the slug in `Name` wrapped one character per line. Below `sm` the table keeps the
                four columns you would scan a flow list for; the rest are a tap away on the flow
                itself. */}
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th visibleFrom="sm">Name</Table.Th>
              <Table.Th visibleFrom="sm">Created</Table.Th>
              <Table.Th ta="right">Calls</Table.Th>
              <Table.Th ta="right">Spend</Table.Th>
              <Table.Th ta="right" visibleFrom="sm">Avg</Table.Th>
              <Table.Th visibleFrom="sm">Activity (30d)</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {flows.map((p) => (
              <Table.Tr
                onClick={() => {
                  setLocation(`/flows/${p.id}`);
                }}
                style={{ cursor: "pointer" }}
                key={p.id}
              >
                <Table.Td>
                  <Tooltip label={p.description}>
                    <Text>{p.title}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td visibleFrom="sm">
                  <Code>{p.name}</Code>
                </Table.Td>
                <Table.Td visibleFrom="sm">
                  <ReactTimeago date={p.created} />
                </Table.Td>
                <Table.Td ta="right">
                  {!isLoadingStats && summaries[p.name] ? (
                    <NumberFormatter value={summaries[p.name].count} thousandSeparator />
                  ) : isLoadingStats ? null : (
                    <Text size="xs" c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td ta="right">
                  {!isLoadingStats && summaries[p.name] ? (
                    <NumberFormatter prefix="$" value={summaries[p.name].costUsd} thousandSeparator decimalScale={2} />
                  ) : isLoadingStats ? null : (
                    <Text size="xs" c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td ta="right" visibleFrom="sm">
                  {!isLoadingStats && summaries[p.name]?.count > 0 ? (
                    <NumberFormatter prefix="$" value={summaries[p.name].costUsd / summaries[p.name].count} thousandSeparator decimalScale={4} />
                  ) : isLoadingStats ? null : (
                    <Text size="xs" c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td visibleFrom="sm">
                  {isLoadingStats ? (
                    <Loader size="sm" />
                  ) : sparklines[p.name] ? (
                    <Sparkline
                      h={40}
                      w={180}
                      data={sparklines[p.name].map((x) => x.value)}
                    />
                  ) : (
                    <Text size="xs" c="dimmed">No data</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={(e) => {
                      e.stopPropagation();
                      openConfirmModal(() => {
                        // setDeleting(true);
                        deleteFlow(p.id).then(() => {
                          setFlows((prev) =>
                            prev.filter((flow) => flow.id !== p.id)
                          );
                          // setDeleting(false);
                        });
                      });
                      // setConfirmOpen(true);
                      // how to pass the id of the flow to delete?
                    }}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Container>
  );
}
